import { getFormStoreSingleton } from '../../store/store';
import { ArrayFieldStateGetters } from '../../store/types';
import { $internals as $formInternals } from '../../form/types';
import { assertFormInitialized } from '../../form/utils/assertFormInitialized';
import { BaseValidationError } from '../../types';

/** Минимальная часть инстанса формы, которая нужна для чтения состояния поля */
type FormRef = {
    name: string;
    id: string;
    [$formInternals]: {
        formInitializationId: string;
    };
};

/**
 * Создает геттеры состояния поля – объект `fieldInstance.state`
 *
 * Возвращает надмножество с `order`: для полей, не являющихся массивами, он вернет пустой список,
 * но в публичном типе инстанса `order` объявлен только у поля-массива
 */
export function createFieldStateGetters<FieldValue, FieldValidationError extends BaseValidationError>(
    form: FormRef,
    fieldId: string
): ArrayFieldStateGetters<FieldValue, FieldValidationError> {
    const {
        getFieldIsTouched,
        getFieldIsDirty,
        getFieldIsValid,
        getFieldIsValidating,
        getFieldIsActionsRunning,
        getFieldErrors,
        getFieldErrorsMap,
        getFieldInitialValue,
        getFieldValidationMeta,
        getFieldAsyncActionsMeta,
        getFieldModifiedAt,
        getFieldOrder,
    } = getFormStoreSingleton(form.name);

    function assertInitialized() {
        assertFormInitialized({
            formName: form.name,
            formId: form.id,
            formInitializationId: form[$formInternals].formInitializationId,
        });
    }

    function getParams() {
        return { formInitializationId: form[$formInternals].formInitializationId, fieldId };
    }

    return {
        isTouched: () => {
            assertInitialized();

            return getFieldIsTouched(getParams());
        },
        isDirty: () => {
            assertInitialized();

            return getFieldIsDirty(getParams());
        },
        isValid: () => {
            assertInitialized();

            return getFieldIsValid(getParams());
        },
        isValidating: () => {
            assertInitialized();

            return getFieldIsValidating(getParams());
        },
        isActionsRunning: () => {
            assertInitialized();

            return getFieldIsActionsRunning(getParams());
        },
        errors: () => {
            assertInitialized();

            // Ошибки в сторе не типизированы, но мы гарантируем тип публичным интерфейсом инстанса
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
            return getFieldErrors(getParams()) as FieldValidationError[];
        },
        errorsMap: () => {
            assertInitialized();

            // Ошибки в сторе не типизированы, но мы гарантируем тип публичным интерфейсом инстанса
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
            return getFieldErrorsMap(getParams()) as ReturnType<
                ArrayFieldStateGetters<FieldValue, FieldValidationError>['errorsMap']
            >;
        },
        initialValue: () => {
            assertInitialized();

            // Значения в сторе не типизированы, но мы гарантируем тип публичным интерфейсом инстанса
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
            return getFieldInitialValue(getParams()) as FieldValue;
        },
        validationMeta: () => {
            assertInitialized();

            return getFieldValidationMeta(getParams());
        },
        asyncActionsMeta: () => {
            assertInitialized();

            return getFieldAsyncActionsMeta(getParams());
        },
        modifiedAt: () => {
            assertInitialized();

            return getFieldModifiedAt(getParams());
        },
        order: () => {
            assertInitialized();

            return getFieldOrder(getParams()) ?? [];
        },
    };
}
